import React, { useEffect, useState, useRef } from "react";
import "../../Styles/CommonStyles.scss";
import {
  Button,
  Label,
  PageLink,
  SecondaryWhiteButton,
} from "../StyledComp/StyledComp";
import DSBAlogo from "../../Images/DSBAlogo.png";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ReactToPrint, { useReactToPrint } from "react-to-print";
import eventApiSlice, { IEvent } from "../../store/eventApiSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import dayjs from "dayjs";
import ReportServices from "../../services/ReportServices";
import AlertWithModal from "../utilityComponents/AlertWithModal";
import * as XLSX from "xlsx";
import htmlToXlsx from "html-to-xlsx";
import saveAs from "file-saver";
import { Select, MenuItem } from "@mui/material";

interface Iprops {
  callBackClose: () => void;
}

const CLEAttendanceReport: React.FC<Iprops> = (props) => {
  const BeforeReportRef = useRef<HTMLDivElement>(null);
  const AfterReportRef = useRef<HTMLDivElement>(null);
  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
  const [alertBody, setAlertBody] = useState<string>("");
  const [alertTitle, setAlertTitle] = useState<string>("");
  const eventInfo: IEvent = useSelector(
    (state: RootState) => state.eventapislice.event
  );

  const [eventState, setEventState] = useState<string>(
    eventInfo.StartDateTime && dayjs(eventInfo.StartDateTime) > dayjs()
      ? "Before"
      : "After"
  );

  const [CLEAttendanceReportBeforeEvent, setCLEAttendanceReportBeforeEvent] =
    useState<any>();
  const [CLEAttendanceReportAfterEvent, setCLEAttendanceReportAfterEvent] =
    useState<any>();

  const [IsActiveDataLoaded, setIsActiveDataLoaded] = useState<boolean>(false);
  const [locationList, setLocationList] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [
    rawCLEAttendanceReportBeforeEvent,
    setRawCLEAttendanceReportBeforeEvent,
  ] = useState<any[]>([]);
  const [
    rawCLEAttendanceReportAfterEvent,
    setRawCLEAttendanceReportAfterEvent,
  ] = useState<any[]>([]);

  // Fetch data only once per eventState/eventInfo.Id
  useEffect(() => {
    setIsActiveDataLoaded(true);
    const fetchData = async () => {
      try {
        if (eventState === "Before") {
          const response =
            await ReportServices.getCLEAttendanceReportBeforeEvent(
              eventInfo.Id!
            );
          if (response.data.data != null) {
            if (response.data.hasErrors) {
              setShowAlertModal(true);
              setAlertTitle("Database Error");
              setAlertBody(response.data.errors[0].sqlMessage);
              setIsActiveDataLoaded(false);
              return;
            }
            const rawList = response.data.data.ParticipantsInfo || [];
            setRawCLEAttendanceReportBeforeEvent(rawList);
            setCLEAttendanceReportBeforeEvent(rawList);
            // Extract unique locations
            const uniqueLocations = Array.from(
              new Set(rawList.map((item: any) => item.Location).filter(Boolean))
            );
            setLocationList(uniqueLocations as string[]);
          } else {
            setRawCLEAttendanceReportBeforeEvent([]);
            setCLEAttendanceReportBeforeEvent([]);
            setLocationList([]);
          }
        } else if (eventState === "After") {
          const response =
            await ReportServices.getCLEAttendanceReportAfterEvent(
              eventInfo.Id!
            );
          if (response.data.data != null) {
            if (response.data.hasErrors) {
              setShowAlertModal(true);
              setAlertTitle("Database Error");
              setAlertBody(response.data.errors[0].sqlMessage);
              setIsActiveDataLoaded(false);
              return;
            }
            const rawList = response.data.data.ParticipantsInfo || [];
            setRawCLEAttendanceReportAfterEvent(rawList);
            setCLEAttendanceReportAfterEvent(rawList);
            // Extract unique locations
            const uniqueLocations = Array.from(
              new Set(rawList.map((item: any) => item.Location).filter(Boolean))
            );
            setLocationList(uniqueLocations as string[]);
          } else {
            setRawCLEAttendanceReportAfterEvent([]);
            setCLEAttendanceReportAfterEvent([]);
            setLocationList([]);
          }
        }
      } catch (error) {
        setRawCLEAttendanceReportBeforeEvent([]);
        setRawCLEAttendanceReportAfterEvent([]);
        setCLEAttendanceReportBeforeEvent([]);
        setCLEAttendanceReportAfterEvent([]);
        setLocationList([]);
      } finally {
        setIsActiveDataLoaded(false);
      }
    };
    fetchData();
  }, [eventState, eventInfo.Id]);

  // Filtered report data based on selectedLocation
  useEffect(() => {
    if (eventState === "Before") {
      if (!selectedLocation) {
        setCLEAttendanceReportBeforeEvent(rawCLEAttendanceReportBeforeEvent);
      } else {
        setCLEAttendanceReportBeforeEvent(
          rawCLEAttendanceReportBeforeEvent.filter(
            (item: any) => item.Location === selectedLocation
          )
        );
      }
    } else if (eventState === "After") {
      if (!selectedLocation) {
        setCLEAttendanceReportAfterEvent(rawCLEAttendanceReportAfterEvent);
      } else {
        setCLEAttendanceReportAfterEvent(
          rawCLEAttendanceReportAfterEvent.filter(
            (item: any) => item.Location === selectedLocation
          )
        );
      }
    }
  }, [
    selectedLocation,
    eventState,
    rawCLEAttendanceReportBeforeEvent,
    rawCLEAttendanceReportAfterEvent,
  ]);

  const handleLocationChange = (value: string) => {
    setSelectedLocation(value);
  };

  const handlePrint = useReactToPrint(
    eventState === "Before"
      ? {
          content: () => BeforeReportRef.current,
        }
      : {
          content: () => AfterReportRef.current,
        }
  );

  const handleExport = async () => {
    let reportRef: any;
    reportRef = eventState === "Before" ? BeforeReportRef : AfterReportRef;
    const ws = XLSX.utils.table_to_sheet(reportRef.current);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    let fileName = "CLEAttendanceReport.xlsx";
    if (eventInfo?.UniqueEventId) {
      fileName = `${eventInfo.UniqueEventId}_CLEAttendanceReport.xlsx`;
    }
    saveAs(blob, fileName);
  };

  return (
    <>
      <div
        className="horizontal-wrapper-spacebetween"
        style={{ alignItems: "center" }}
      >
        <h1 className="page-heading" style={{ height: "auto" }}>
          CLE Attendance Report
        </h1>
        <div
          style={{
            display: "flex",
            justifyContent: "start",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <div key="EventState" className="checkboxgroup">
            <input
              id="Before Event"
              className="checkbox"
              type="checkbox"
              checked={eventState === "Before" ? true : false}
              onChange={() => setEventState("Before")}
            />
            <span
              style={{
                marginRight: "3rem",
                fontSize: "1.25rem",
                fontWeight: "500",
              }}
            >
              Before Event
            </span>
          </div>
          <div key="EventState" className="checkboxgroup">
            <input
              id="After Event"
              className="checkbox"
              type="checkbox"
              checked={eventState === "After" ? true : false}
              onChange={() => setEventState("After")}
            />
            <span
              style={{
                marginRight: "3rem",
                fontSize: "1.25rem",
                fontWeight: "500",
              }}
            >
              After Event
            </span>
          </div>
        </div>
        <div
          className="horizontal-wrapper-rightaligned"
          style={{
            width: "auto",
            marginLeft: "0px",
            marginRight: "0px",
          }}
        >
          <SecondaryWhiteButton
            className="button-wrapper"
            type="button"
            style={{
              marginRight: ".5rem",
              marginLeft: ".5rem",
            }}
            onClick={() => handleExport()}
            // onClick={() => handlePrint()}
          >
            <span style={{ fontWeight: "500" }}>Download as Excel</span>
          </SecondaryWhiteButton>
          <SecondaryWhiteButton
            className="button-wrapper"
            type="button"
            style={{
              marginRight: ".5rem",
              marginLeft: ".5rem",
            }}
            onClick={() => handlePrint()}
          >
            <span style={{ fontWeight: "500" }}>Print / Download</span>
          </SecondaryWhiteButton>
          <PageLink
            onClick={props.callBackClose}
            // style={{ marginBottom: "2rem" }}
          >
            Close
          </PageLink>
        </div>
        {eventState === "Before" ? (
          <div className="section-box" ref={BeforeReportRef}>
            <div
              className="grid-report-header-container"
              style={{ alignItems: "center", marginBottom: "2rem" }}
            >
              <div>
                <img className="logo-img " src={DSBAlogo} alt="DSBA" />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <h2 style={{ height: "auto" }}>
                  CLE Attendance Report (Before Event)
                </h2>
              </div>
            </div>
            <div
              className="form-wrapper-vertical "
              style={{ width: "100%", marginBottom: "2rem" }}
            >
              <div className="grid-report-title-container">
                <div className="grid-report-title-item1">Event</div>
                <div>:</div>
                <div className="grid-report-title-item2">
                  {eventInfo.EventName}
                </div>
                <div className="grid-report-title-item1">Event ID</div>
                <div>:</div>
                <div className="grid-report-title-item2">
                  {eventInfo.UniqueEventId}
                </div>
                <div className="grid-report-title-item1">Date</div>
                <div>:</div>
                <div className="grid-report-title-item2">
                  {dayjs(eventInfo.StartDateTime)?.format("DD-MMM-YYYY")}
                </div>
                <div className="grid-report-title-item1">Location</div>
                <div>:</div>
                <div className="grid-report-title-item2">
                  <Select
                    value={selectedLocation}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    displayEmpty
                    size="small"
                    style={{ minWidth: 180 }}
                  >
                    <MenuItem value="">
                      <em>All Locations</em>
                    </MenuItem>
                    {locationList.map((loc) => (
                      <MenuItem key={loc} value={loc}>
                        {loc}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div style={{ width: "100%" }}>
              <table
                className="eventlist-table "
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead className="table-border">
                  <tr style={{ height: "auto", whiteSpace: "normal" }}>
                    <th style={{ width: "5%" }}>
                      <input type="checkbox" name="allselect" />
                    </th>
                    {/* <th style={{ width: "7%" }}>Judge?</th> */}
                    <th style={{ width: "15%", textAlign: "center" }}>BarID</th>
                    <th style={{ width: "19%", textAlign: "center" }}>
                      Full Name
                    </th>
                    <th style={{ width: "18%", textAlign: "center" }}>
                      Location
                    </th>
                    <th style={{ width: "18%", textAlign: "center" }}>
                      Payment Status
                    </th>
                    <th style={{ width: "15%", textAlign: "center" }}>
                      Date Paid
                    </th>
                    <th style={{ width: "8%", textAlign: "center" }}>PA Cr</th>
                    <th style={{ width: "13%", textAlign: "center" }}>
                      Time In{" "}
                    </th>
                    <th style={{ width: "13%", textAlign: "center" }}>
                      Time Out
                    </th>
                  </tr>
                </thead>
                {IsActiveDataLoaded === false ? (
                  <tbody className="table-border">
                    {/* {Array.from({ length: 45 }).map((_, index) => ( */}
                    {CLEAttendanceReportBeforeEvent &&
                      CLEAttendanceReportBeforeEvent.map(
                        (reportLineItem: any) => (
                          <tr style={{ height: "auto", whiteSpace: "normal" }}>
                            <td style={{ width: "2%" }}>
                              <input type="checkbox" />
                            </td>
                            <td
                              style={{
                                width: "15%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem && reportLineItem.BarId
                                ? reportLineItem.BarId
                                : null}
                            </td>
                            <td
                              style={{
                                width: "19%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem && reportLineItem.LastName
                                ? reportLineItem.LastName
                                : null}
                              ,{" "}
                              {reportLineItem && reportLineItem.FirstName
                                ? reportLineItem.FirstName
                                : null}
                            </td>
                            <td
                              style={{
                                width: "18%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem && reportLineItem.Location
                                ? reportLineItem.Location
                                : null}
                            </td>
                            <td
                              style={{
                                width: "18%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem.ParticipantType === "Speaker" ||
                              reportLineItem.ParticipantType === "Moderator"
                                ? "-"
                                : (reportLineItem.AmountPaid ?? 0) +
                                    (reportLineItem.CouponAmount ?? 0) ===
                                  0
                                ? "Pending"
                                : reportLineItem.TotalAmount == null ||
                                  (reportLineItem.AmountPaid ?? 0) +
                                    (reportLineItem.CouponAmount ?? 0) <
                                    reportLineItem.TotalAmount!!
                                ? "Partially Paid"
                                : "Paid"}
                            </td>
                            <td
                              style={{
                                width: "15%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem && reportLineItem.DatePaid
                                ? dayjs(reportLineItem.DatePaid).format(
                                    "DD-MMM-YYYY"
                                  )
                                : null}
                            </td>
                            <td style={{ width: "8%" }}>
                              <input type="checkbox" />
                            </td>
                            <td
                              style={{
                                width: "13%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            ></td>
                            <td
                              style={{
                                width: "13%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            ></td>
                          </tr>
                        )
                      )}
                  </tbody>
                ) : (
                  <>Loading...</>
                )}
              </table>
            </div>
          </div>
        ) : (
          <div className="section-box" ref={AfterReportRef}>
            <div
              className="grid-report-header-container"
              style={{ alignItems: "center", marginBottom: "2rem" }}
            >
              <div>
                <img className="logo-img " src={DSBAlogo} alt="DSBA" />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <h2 style={{ height: "auto" }}>
                  CLE Attendance Report (After Event)
                </h2>
              </div>
            </div>
            <div
              className="form-wrapper-vertical "
              style={{ width: "100%", marginBottom: "2rem" }}
            >
              <div className="grid-report-title-container">
                <div className="grid-report-title-item1">Event</div>
                <div>:</div>
                <div className="grid-report-title-item2">
                  {eventInfo.EventName}
                </div>
                <div className="grid-report-title-item1">Event Id</div>
                <div>:</div>
                <div className="grid-report-title-item2">
                  {eventInfo.UniqueEventId}
                </div>
                <div className="grid-report-title-item1">Date</div>
                <div>:</div>
                <div className="grid-report-title-item2">
                  {dayjs(eventInfo.StartDateTime)?.format("DD-MMM-YYYY")}
                </div>
                <div className="grid-report-title-item1">Location</div>
                <div>:</div>
                <div className="grid-report-title-item2">
                  <Select
                    value={selectedLocation}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    displayEmpty
                    size="small"
                    style={{ minWidth: 180 }}
                  >
                    <MenuItem value="">
                      <em>All Locations</em>
                    </MenuItem>
                    {locationList.map((loc) => (
                      <MenuItem key={loc} value={loc}>
                        {loc}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div style={{ width: "100%" }}>
              <table
                className="eventlist-table "
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead className="table-border">
                  <tr
                    style={{
                      height: "auto",
                      whiteSpace: "normal",
                    }}
                  >
                    {/* <th style={{ width: "5%" }}>
                      <input type="checkbox" name="allselect" />
                    </th> */}
                    {/* <th style={{ width: "7%" }}>Judge?</th> */}
                    <th style={{ width: "16%", textAlign: "center" }}>BarID</th>
                    <th style={{ width: "24%", textAlign: "center" }}>
                      Full Name
                    </th>
                    <th style={{ width: "16%", textAlign: "center" }}>
                      Start Date
                    </th>
                    <th style={{ width: "16%", textAlign: "center" }}>
                      End Date
                    </th>
                    <th style={{ width: "16%", textAlign: "center" }}>
                      Payment Status
                    </th>
                    <th style={{ width: "7%", textAlign: "center" }}>DE</th>
                    <th style={{ width: "7%", textAlign: "center" }}>DE-En</th>
                    <th style={{ width: "7%", textAlign: "center" }}>PA</th>
                    <th style={{ width: "7%", textAlign: "center" }}>PA-En</th>
                    <th style={{ width: "7%", textAlign: "center" }}>NJ</th>
                    <th style={{ width: "7%", textAlign: "center" }}>NJ-En</th>
                  </tr>
                </thead>
                {IsActiveDataLoaded === false ? (
                  <tbody className="table-border">
                    {/* {Array.from({ length: 45 }).map((_, index) => ( */}
                    {CLEAttendanceReportAfterEvent &&
                      CLEAttendanceReportAfterEvent.map(
                        (reportLineItem: any) => (
                          <tr style={{ height: "auto", whiteSpace: "normal" }}>
                            <td
                              style={{
                                width: "16%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem && reportLineItem.BarId
                                ? reportLineItem.BarId
                                : null}
                            </td>
                            <td
                              style={{
                                width: "24%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem && reportLineItem.LastName
                                ? reportLineItem.LastName
                                : null}
                              ,{" "}
                              {reportLineItem && reportLineItem.FirstName
                                ? reportLineItem.FirstName
                                : null}
                            </td>
                            <td
                              style={{
                                width: "16%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem && reportLineItem.StartDate
                                ? dayjs(reportLineItem.StartDate).format(
                                    "DD-MMM-YYYY"
                                  )
                                : null}
                            </td>
                            <td
                              style={{
                                width: "16%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                              }}
                            >
                              {reportLineItem && reportLineItem.EndDate
                                ? dayjs(reportLineItem.EndDate).format(
                                    "DD-MMM-YYYY"
                                  )
                                : null}
                            </td>
                            <td
                              style={{
                                width: "7%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                                textAlign: "center",
                              }}
                            >
                              {" "}
                              {reportLineItem.ParticipantType === "Speaker" ||
                              reportLineItem.ParticipantType === "Moderator"
                                ? "-"
                                : (reportLineItem.AmountPaid ?? 0) +
                                    (reportLineItem.CouponAmount ?? 0) ===
                                  0
                                ? "Pending"
                                : reportLineItem.TotalAmount == null ||
                                  (reportLineItem.AmountPaid ?? 0) +
                                    (reportLineItem.CouponAmount ?? 0) <
                                    reportLineItem.TotalAmount!!
                                ? "Partially Paid"
                                : "Paid"}
                            </td>
                            <td
                              style={{
                                width: "7%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                                textAlign: "center",
                              }}
                            >
                              {reportLineItem && reportLineItem.DECredits
                                ? reportLineItem.DECredits
                                : null}
                            </td>
                            <td
                              style={{
                                width: "7%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                                textAlign: "center",
                              }}
                            >
                              {reportLineItem && reportLineItem.DEEnchEthics
                                ? reportLineItem.DEEnchEthics
                                : null}
                            </td>
                            <td
                              style={{
                                width: "7%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                                textAlign: "center",
                              }}
                            >
                              {reportLineItem && reportLineItem.PACredits
                                ? reportLineItem.PACredits
                                : null}
                            </td>
                            <td
                              style={{
                                width: "7%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                                textAlign: "center",
                              }}
                            >
                              {reportLineItem && reportLineItem.PAEthics
                                ? reportLineItem.PAEthics
                                : null}
                            </td>
                            <td
                              style={{
                                width: "7%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                                textAlign: "center",
                              }}
                            >
                              {reportLineItem && reportLineItem.NJCredits
                                ? reportLineItem.NJCredits
                                : null}
                            </td>
                            <td
                              style={{
                                width: "7%",
                                overflow: "wrap",
                                wordWrap: "break-word",
                                textAlign: "center",
                              }}
                            >
                              {reportLineItem && reportLineItem.NJEthics
                                ? reportLineItem.NJEthics
                                : null}
                            </td>
                          </tr>
                        )
                      )}
                  </tbody>
                ) : (
                  <>Loading...</>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
      {showAlertModal ? (
        <AlertWithModal
          title={alertTitle}
          body={alertBody}
          show={showAlertModal}
          onClose={() => setShowAlertModal(false)} // Close the modal
        />
      ) : (
        <></>
      )}
    </>
  );
};
export default CLEAttendanceReport;
